import React, { useState, useRef, useEffect } from 'react';
import {
    Stethoscope, UserPlus, FlaskConical, Scale, Pill, Mail, User, MapPin,
    KeyRound, CheckCircle2, AlertCircle, X, ChevronRight,
    Loader2, Sparkles, Search, History, RotateCcw, List
} from 'lucide-react';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import addDays from 'date-fns/addDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import { THEME } from './theme/themeColors';

import {
    getCompanyByAccountType,
    getNextCompanyPrefix,
    registerAccount,
    getRegistrationStatus,
    getRegistrationHistory,
    rollbackRegistration,
} from './services/masterDataService';

const ACCOUNT_TYPE_API_MAP = {
    law_firm: 0,
    referring: 1,
    testing_facility: 1,
    medical: 2,
    pharmacy: 2,
};

// Account-type accents are CATEGORY colours (each type has its own identity) — intentionally NOT themed.
const ACCOUNT_TYPES = [
    { id: 'medical', label: 'Medical', icon: Stethoscope, accent: '#0f766e', accentSoft: '#ccfbf1', prefixLength: 2 },
    { id: 'referring', label: 'Referring', icon: UserPlus, accent: '#1d4ed8', accentSoft: '#dbeafe', prefixLength: 2 },
    { id: 'testing_facility', label: 'Testing Facility', icon: FlaskConical, accent: '#6d28d9', accentSoft: '#ede9fe', prefixLength: 2 },
    { id: 'law_firm', label: 'Law Firm', icon: Scale, accent: '#b45309', accentSoft: '#fef3c7', prefixLength: 3 },
    { id: 'pharmacy', label: 'Pharmacy', icon: Pill, accent: '#be123c', accentSoft: '#ffe4e6', prefixLength: 2 },
];

const EXISTING_COMPANY_NAMES = new Set(['acme diagnostics', 'summit legal group']);
const EXISTING_PREFIXES = new Set(['AC', 'SL', 'RX1']);
const EXISTING_USERNAMES = new Set(['acadmin', 'sladmin']);

const REGISTRATION_DOMAIN = sessionStorage.getItem('selectedDomain') || 'greenyourbills.com';
const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_POLL_TIMEOUT_MS = 60000;
const COMPANY_NAME_INVALID_CHARS_REGEX = /[^a-zA-Z0-9\s&.,'-]/;

const predefinedBottomRanges = [
    { label: 'Today', value: [new Date(), new Date()] },
    { label: 'Yesterday', value: [addDays(new Date(), -1), addDays(new Date(), -1)] },
    { label: 'This week', value: [startOfWeek(new Date()), endOfWeek(new Date())] },
    { label: 'Last 7 days', value: [subDays(new Date(), 6), new Date()] },
    { label: 'Last 30 days', value: [subDays(new Date(), 29), new Date()] },
    { label: 'This month', value: [startOfMonth(new Date()), endOfMonth(new Date())] },
    { label: 'Last month', value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))] },
    { label: 'This year', value: [new Date(new Date().getFullYear(), 0, 1), new Date(new Date().getFullYear(), 11, 31)] },
    { label: 'Last year', value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear(), 0, 0)] },
    { label: 'All time', value: [new Date(new Date().getFullYear() - 5, 0, 1), new Date()] },
];

const getTypeConfig = (id) => ACCOUNT_TYPES.find((t) => t.id === id);

const Field = ({ label, required, htmlFor, error, hint, children }) => (
    <div style={{ marginBottom: '10px' }}>
        <label htmlFor={htmlFor} style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
            {label}{required && <span style={{ color: '#dc2626', marginLeft: '3px' }}>*</span>}
        </label>
        {children}
        {hint && !error && <div style={{ fontSize: '10.5px', color: '#94a3b8', marginTop: '4px' }}>{hint}</div>}
        {error && (
            <div style={{ fontSize: '10.5px', color: '#dc2626', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                <AlertCircle size={11} strokeWidth={2.5} />{error}
            </div>
        )}
    </div>
);

const baseInputStyle = (hasError) => ({
    width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontSize: '12.5px',
    color: '#0f172a', background: '#fff', border: `1.5px solid ${hasError ? '#fca5a5' : '#e2e8f0'}`,
    borderRadius: '7px', outline: 'none', fontFamily: 'inherit',
});

const monoInputStyle = (hasError) => ({
    ...baseInputStyle(hasError),
    fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
    letterSpacing: '0.02em',
});

const SectionHeader = ({ index, title, subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '11px' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10.5px', color: '#cbd5e1', fontWeight: 700 }}>{index}</span>
        <div>
            <h3 style={{ margin: 0, fontSize: '12.5px', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
            {subtitle && <p style={{ margin: '2px 0 0', fontSize: '10.5px', color: '#94a3b8' }}>{subtitle}</p>}
        </div>
    </div>
);

const thStyle = { textAlign: 'left', padding: '8px 5px', fontSize: '10.5px', fontWeight: 600, color: '#64748b' };
const tdStyle = { padding: '8px 5px', color: '#334155', verticalAlign: 'middle', fontSize: '11px' };

// Status colours are SEMANTIC (green = done, red = failed, amber = in progress) — intentionally NOT themed.
function StatusBadge({ status }) {
    const map = {
        Completed: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
        Failed: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
        Processing: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
        Queued: { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' },
        RollbackProcessing: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
        RollbackComplete: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
        RollbackFailed: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    };
    const s = map[status] || map.Queued;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 6px', borderRadius: '999px',
            fontSize: '10px', fontWeight: 600, background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            whiteSpace: 'nowrap'
        }}>
            {status || '—'}
        </span>
    );
}

function Card({ children, style }) {
    return (
        <div className="md-card" style={{
            background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px',
            padding: '14px 15px 8px', boxShadow: '0 1px 2px rgba(15,23,42,0.03)', ...style,
        }}>
            {children}
        </div>
    );
}

function InlineError({ message }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px',
            color: '#dc2626', fontWeight: 500, background: '#fef2f2',
            border: '1px solid #fecaca', borderRadius: '7px', padding: '7px 9px',
        }}>
            <AlertCircle size={11} strokeWidth={2.5} style={{ flexShrink: 0 }} />
            <span>{message}</span>
        </div>
    );
}

function IconInput({ icon: Icon, id, type = 'text', value, onChange, placeholder, hasError, mono }) {
    return (
        <div style={{ position: 'relative' }}>
            <Icon size={13} style={{
                position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                color: '#94a3b8', pointerEvents: 'none',
            }} />
            <input
                id={id} type={type} value={value}
                onChange={(ev) => onChange(ev.target.value)}
                placeholder={placeholder} className="md-input"
                style={{ ...(mono ? monoInputStyle(hasError) : baseInputStyle(hasError)), paddingLeft: '29px' }}
            />
        </div>
    );
}

function SummaryRow({ label, value, mono }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: '#94a3b8' }}>{label}</span>
            <span style={{
                fontWeight: 600, color: '#0f172a', textAlign: 'right', wordBreak: 'break-word',
                fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
            }}>
                {value}
            </span>
        </div>
    );
}

/* ── Custom confirm / result dialogs ───── */
function RollbackConfirmDialog({ record, busy, onCancel, onConfirm }) {
    if (!record) return null;
    return (
        <div
            role="alertdialog" aria-modal="true"
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2000, padding: '16px', animation: 'md-fade-in 0.15s ease',
            }}
            onMouseDown={(ev) => { if (ev.target === ev.currentTarget && !busy) onCancel(); }}
        >
            <div style={{
                width: '100%', maxWidth: '420px', background: '#fff', borderRadius: '16px',
                border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
                padding: '24px', textAlign: 'center', animation: 'md-pop-in 0.16s ease',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    width: '46px', height: '46px', borderRadius: '50%', background: '#fff7ed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
                    boxShadow: '0 0 0 6px rgba(255,247,237,0.6)',
                }}>
                    <RotateCcw size={20} color="#c2410c" strokeWidth={2.3} />
                </div>
                <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    Rollback this registration?
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.55 }}>
                    This will queue a rollback and undo the account setup. This action can't be undone.
                </p>
                <div style={{
                    textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0',
                    borderRadius: '10px', padding: '13px 15px', marginBottom: '20px',
                    fontSize: '12px', display: 'grid', rowGap: '8px',
                }}>
                    <SummaryRow label="Request ID" value={record.requestId} mono />
                    <SummaryRow label="Company" value={record.companyName || '—'} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        type="button" onClick={onCancel} disabled={busy} className="md-btn-secondary"
                        style={{
                            flex: 1, padding: '10px', borderRadius: '9px', border: '1.5px solid #e2e8f0',
                            background: '#fff', color: '#334155', fontSize: '12.5px', fontWeight: 600,
                            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button" onClick={onConfirm} disabled={busy} className="md-btn-danger"
                        style={{
                            flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                            padding: '10px', borderRadius: '9px', border: 'none', background: '#dc2626',
                            color: '#fff', fontSize: '12.5px', fontWeight: 700,
                            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.8 : 1,
                        }}
                    >
                        {busy
                            ? <Loader2 size={13} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                            : <RotateCcw size={13} />}
                        {busy ? 'Rolling back…' : 'Yes, rollback'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RollbackResultDialog({ result, onClose }) {
    if (!result) return null;
    const isSuccess = result.success;
    return (
        <div
            role="alertdialog" aria-modal="true"
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2000, padding: '16px', animation: 'md-fade-in 0.15s ease',
            }}
            onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
        >
            <div style={{
                width: '100%', maxWidth: '420px', background: '#fff', borderRadius: '16px',
                border: '1px solid #e2e8f0', boxShadow: '0 20px 50px rgba(15,23,42,0.25)',
                padding: '24px', textAlign: 'center', animation: 'md-pop-in 0.16s ease',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    width: '46px', height: '46px', borderRadius: '50%',
                    background: isSuccess ? '#f0fdf4' : '#fef2f2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
                    boxShadow: isSuccess ? '0 0 0 6px rgba(240,253,244,0.7)' : '0 0 0 6px rgba(254,242,242,0.7)',
                }}>
                    {isSuccess
                        ? <CheckCircle2 size={20} color="#16a34a" strokeWidth={2.3} />
                        : <AlertCircle size={20} color="#dc2626" strokeWidth={2.3} />}
                </div>
                <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                    {isSuccess ? 'Rollback queued' : 'Rollback failed'}
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.55 }}>
                    {result.message}
                </p>
                {(result.requestId || result.status) && (
                    <div style={{
                        textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: '10px', padding: '13px 15px', marginBottom: '20px',
                        fontSize: '12px', display: 'grid', rowGap: '8px',
                    }}>
                        {result.requestId && <SummaryRow label="Request ID" value={result.requestId} mono />}
                        {result.status && <SummaryRow label="Status" value={result.status} />}
                    </div>
                )}
                <button
                    type="button" onClick={onClose} className="md-btn-primary"
                    style={{
                        width: '100%', padding: '10px', borderRadius: '9px', border: 'none',
                        background: THEME.primary, color: THEME.onPrimary, fontSize: '12.5px', fontWeight: 700, cursor: 'pointer',
                    }}
                >
                    Close
                </button>
            </div>
        </div>
    );
}

export default function AccountRegistration() {
    // ─── Original form state ───────────────────────────────────────────────
    const [accountType, setAccountType] = useState(null);
    const [baseAccount, setBaseAccount] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    const [city, setCity] = useState('');
    const [stateVal, setStateVal] = useState('');
    const [zip, setZip] = useState('');
    const [officePhone, setOfficePhone] = useState('');
    const [companyPrefix, setCompanyPrefix] = useState('');
    const [username, setUsername] = useState('');
    const usernameTouchedRef = useRef(false);
    const [password, setPassword] = useState('');
    const [includeProcedureCodes, setIncludeProcedureCodes] = useState(false);
    const [procedureCodes, setProcedureCodes] = useState(['']);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [createdSummary, setCreatedSummary] = useState(null);
    const [baseAccountOptions, setBaseAccountOptions] = useState([]);
    const [baseAccountsLoading, setBaseAccountsLoading] = useState(false);
    const [baseAccountsError, setBaseAccountsError] = useState(null);
    const [baseAccountSearch, setBaseAccountSearch] = useState('');
    const [baseAccountDropdownOpen, setBaseAccountDropdownOpen] = useState(false);
    const baseAccountFieldRef = useRef(null);
    const [prefixSuggestLoading, setPrefixSuggestLoading] = useState(false);
    const [prefixSuggestError, setPrefixSuggestError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [trackingStatus, setTrackingStatus] = useState(null);
    const [statusPolling, setStatusPolling] = useState(false);
    const pollAbortRef = useRef(null);
    const typeConfig = accountType ? getTypeConfig(accountType) : null;

    // ─── History Modal state ───────────────────────────────────────────────
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyRecords, setHistoryRecords] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [historyFilterStatus, setHistoryFilterStatus] = useState('');
    const [historyFilterType, setHistoryFilterType] = useState('');
    const [historyDateRange, setHistoryDateRange] = useState({ from: '', to: '' });
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPageSize, setHistoryPageSize] = useState(20);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [rollingBackId, setRollingBackId] = useState(null);
    const [expandedStepId, setExpandedStepId] = useState(null);

    // ─── Rollback dialog state ──────────────────────────────────────────────
    const [rollbackConfirmRecord, setRollbackConfirmRecord] = useState(null);
    const [rollbackResult, setRollbackResult] = useState(null);

    // ─── Original effects ──────────────────────────────────────────────────
    useEffect(() => {
        if (!accountType) {
            setBaseAccountOptions([]);
            setBaseAccountsError(null);
            setBaseAccountsLoading(false);
            return;
        }
        const apiType = ACCOUNT_TYPE_API_MAP[accountType];
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            setBaseAccountsLoading(true);
            setBaseAccountsError(null);
            try {
                const companies = await getCompanyByAccountType(apiType, REGISTRATION_DOMAIN, { signal: controller.signal });
                if (!cancelled) setBaseAccountOptions(companies ?? []);
            } catch (e) {
                if (cancelled || e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError') return;
                if (e.response?.status === 404) setBaseAccountOptions([]);
                else {
                    setBaseAccountsError('Failed to load base accounts. Please try again.');
                    setBaseAccountOptions([]);
                }
            } finally {
                if (!cancelled) setBaseAccountsLoading(false);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, [accountType]);

    useEffect(() => () => { if (pollAbortRef.current) pollAbortRef.current.abort(); }, []);

    useEffect(() => {
        const handleClickOutside = (ev) => {
            if (baseAccountFieldRef.current && !baseAccountFieldRef.current.contains(ev.target)) {
                setBaseAccountDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dialogs on Escape
    useEffect(() => {
        const handleKeyDown = (ev) => {
            if (ev.key !== 'Escape') return;
            if (rollbackResult) setRollbackResult(null);
            else if (rollbackConfirmRecord && rollingBackId !== rollbackConfirmRecord.requestId) setRollbackConfirmRecord(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [rollbackResult, rollbackConfirmRecord, rollingBackId]);

    // ─── History load ──────────────────────────────────────────────────────
    const loadHistory = async (page = 1) => {
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const data = await getRegistrationHistory({
                domain: REGISTRATION_DOMAIN,
                status: historyFilterStatus || undefined,
                accountType: historyFilterType || undefined,
                startDate: historyDateRange.from || undefined,
                endDate: historyDateRange.to || undefined,
                pageNumber: page,
                pageSize: historyPageSize,
            });
            const records = Array.isArray(data) ? data : [];
            setHistoryRecords(records);
            setHistoryHasMore(records.length === historyPageSize);
            setHistoryPage(page);
        } catch (err) {
            if (err.response?.status === 404) {
                setHistoryRecords([]);
                setHistoryHasMore(false);
                setHistoryError(null);
            } else {
                setHistoryError(err.response?.data?.message || err.message || 'Failed to load registration history.');
                setHistoryRecords([]);
                setHistoryHasMore(false);
            }
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (historyOpen) loadHistory(1);
    }, [historyOpen, historyFilterStatus, historyFilterType, historyDateRange, historyPageSize]);

    // ─── Rollback handlers ──────────────────────────────────────────────────
    const requestRollback = (record) => {
        setRollbackConfirmRecord(record);
    };

    const cancelRollbackConfirm = () => {
        if (rollingBackId) return;
        setRollbackConfirmRecord(null);
    };

    const confirmRollback = async () => {
        const record = rollbackConfirmRecord;
        if (!record) return;
        setRollingBackId(record.requestId);
        try {
            const result = await rollbackRegistration(record);
            setRollbackConfirmRecord(null);
            setRollbackResult({
                success: true,
                message: 'The rollback has been queued successfully.',
                status: result?.status || 'Rollback Queued',
                requestId: result?.requestId || record.requestId,
            });
            await loadHistory(historyPage);
        } catch (err) {
            setRollbackConfirmRecord(null);
            setRollbackResult({
                success: false,
                message: err.response?.data?.message || err.message || 'Rollback request failed. Please try again.',
                requestId: record.requestId,
            });
        } finally {
            setRollingBackId(null);
        }
    };

    const closeRollbackResult = () => setRollbackResult(null);

    // ─── Original helpers ──────────────────────────────────────────────────
    const applyPrefix = (raw) => {
        const val = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
        setCompanyPrefix(val);
        if (val) setUsername(`${val}Admin`);
        else setUsername('');
    };

    const canSuggestPrefix = !!typeConfig;

    const handleSuggestPrefix = async () => {
        if (!typeConfig) return;
        setPrefixSuggestLoading(true);
        setPrefixSuggestError(null);
        try {
            const next = await getNextCompanyPrefix(typeConfig.prefixLength, REGISTRATION_DOMAIN);
            if (next) applyPrefix(next);
        } catch (e) {
            setPrefixSuggestError(e.response?.data?.message || 'Failed to fetch a suggested prefix.');
        } finally {
            setPrefixSuggestLoading(false);
        }
    };

    const handleUsernameChange = (val) => {
        usernameTouchedRef.current = true;
        setUsername(val);
    };

    const handleTypeSelect = (id) => {
        setAccountType(id);
        setBaseAccount('');
        setBaseAccountSearch('');
        setCompanyName('');
        setUsername('');
        setCompanyPrefix('');
    };

    const usernameConflict = username && EXISTING_USERNAMES.has(username.toLowerCase());

    const selectedBaseAccountLabel = (() => {
        const match = baseAccountOptions.find((ba) => ba.companyID === baseAccount);
        return match ? `${match.companyName} - ${match.companyID}` : '';
    })();

    const filteredBaseAccountOptions = (() => {
        const term = baseAccountSearch.trim().toLowerCase();
        if (!term) return baseAccountOptions;
        return baseAccountOptions.filter((ba) =>
            `${ba.companyName} ${ba.companyID}`.toLowerCase().includes(term)
        );
    })();

    const handleBaseAccountInputChange = (val) => {
        setBaseAccountSearch(val);
        setBaseAccountDropdownOpen(true);
        if (baseAccount) setBaseAccount('');
    };

    const handleBaseAccountSelect = (ba) => {
        setBaseAccount(ba.companyID);
        setBaseAccountSearch(`${ba.companyName} - ${ba.companyID}`);
        setBaseAccountDropdownOpen(false);
    };

    const validate = () => {
        const e = {};
        if (!accountType) e.accountType = 'Select an account type to continue.';
        const trimmedCompanyName = companyName.trim();
        if (!trimmedCompanyName) e.companyName = 'Company Name is required.';
        else if (COMPANY_NAME_INVALID_CHARS_REGEX.test(trimmedCompanyName))
            e.companyName = "Company Name can only contain letters, numbers, spaces, and & . , ' -";
        else if (EXISTING_COMPANY_NAMES.has(trimmedCompanyName.toLowerCase()))
            e.companyName = 'This company name is already registered.';
        if (!baseAccount) e.baseAccount = 'Select a Base Account.';
        else if (!baseAccountOptions.some((ba) => ba.companyID === baseAccount))
            e.baseAccount = 'Selected Base Account is no longer valid for this Account Type.';
        if (!email.trim()) e.email = 'Email Address is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Enter a valid email address.';
        if (!stateVal.trim()) e.state = 'State is required.';
        const expectedLen = typeConfig ? typeConfig.prefixLength : null;
        if (!companyPrefix.trim()) e.companyPrefix = 'Company Prefix is required.';
        else if (expectedLen && companyPrefix.trim().length !== expectedLen)
            e.companyPrefix = `Prefix must be exactly ${expectedLen} characters for ${typeConfig.label}.`;
        else if (EXISTING_PREFIXES.has(companyPrefix.trim().toUpperCase()))
            e.companyPrefix = 'This prefix is already in use.';
        if (!username.trim()) e.username = 'Username is required.';
        else if (EXISTING_USERNAMES.has(username.trim().toLowerCase()))
            e.username = 'This username already exists. Enter a unique username.';
        if (!password) e.password = 'Password is required.';
        else if (password.length < 8) e.password = 'Password must be at least 8 characters.';
        return e;
    };

    const pollStatus = async (requestId) => {
        const controller = new AbortController();
        pollAbortRef.current = controller;
        setStatusPolling(true);
        const startedAt = Date.now();
        try {
            while (!controller.signal.aborted) {
                if (Date.now() - startedAt > STATUS_POLL_TIMEOUT_MS) {
                    setTrackingStatus((prev) => prev ?? 'Queued');
                    break;
                }
                const result = await getRegistrationStatus(requestId, REGISTRATION_DOMAIN, { signal: controller.signal });
                if (result?.status) {
                    setTrackingStatus(result.status);
                    if (['Completed', 'Failed', 'Error', 'RollbackComplete', 'RollbackFailed'].includes(result.status)) break;
                }
                await new Promise((r) => setTimeout(r, STATUS_POLL_INTERVAL_MS));
            }
        } catch (err) {
            if (err.name !== 'AbortError') console.error('Status polling failed', err);
        } finally {
            setStatusPolling(false);
        }
    };

    const handleSubmit = async (evt) => {
        evt.preventDefault();
        const e = validate();
        setErrors(e);
        setSubmitted(true);
        setSubmitError(null);
        if (Object.keys(e).length > 0) return;

        const trimmedContact = contactName.trim();
        const [contactFirstName, ...rest] = trimmedContact ? trimmedContact.split(/\s+/) : [''];
        const contactLastName = rest.join(' ');
        const Email = sessionStorage.getItem('userEmail') || '';
        const selectedCompany = baseAccountOptions.find((ba) => ba.companyID === baseAccount);

        const dto = {
            accountType,
            companyName: companyName.trim(),
            baseAccount,
            contactFirstName: contactFirstName || '',
            contactLastName: contactLastName || '',
            email: email.trim(),
            address1: address1.trim(),
            address2: address2.trim(),
            city: city.trim(),
            zip: zip.trim(),
            state: stateVal.trim(),
            companyPrefix: companyPrefix.trim().toUpperCase(),
            domain: REGISTRATION_DOMAIN,
            password,
            username: username.trim(),
            officePhone: officePhone.trim(),
            procedureCodes: includeProcedureCodes,
            ReceiverEmail: Email,
            isRollback: false,
        };

        setSubmitting(true);
        try {
            const result = await registerAccount(dto);
            setCreatedSummary({
                requestId: result.requestId,
                companyName: dto.companyName,
                accountType: typeConfig.label,
                baseAccount: selectedCompany ? selectedCompany.companyName : baseAccount,
                username: dto.username,
                prefix: dto.companyPrefix,
                procedureCodes: dto.procedureCodes,
            });
            setTrackingStatus(result.status ?? 'Queued');
            pollStatus(result.requestId);
        } catch (err) {
            setSubmitError(err.message || 'Something went wrong submitting the registration. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        if (pollAbortRef.current) pollAbortRef.current.abort();
        setAccountType(null);
        setBaseAccount('');
        setBaseAccountSearch('');
        setBaseAccountDropdownOpen(false);
        setCompanyName('');
        setContactName('');
        setEmail('');
        setAddress1(''); setAddress2(''); setCity(''); setStateVal(''); setZip('');
        setOfficePhone('');
        setCompanyPrefix('');
        setUsername('');
        usernameTouchedRef.current = false;
        setPassword('');
        setIncludeProcedureCodes(false);
        setProcedureCodes(['']);
        setErrors({});
        setSubmitted(false);
        setCreatedSummary(null);
        setBaseAccountOptions([]);
        setBaseAccountsError(null);
        setPrefixSuggestError(null);
        setSubmitError(null);
        setTrackingStatus(null);
        setStatusPolling(false);
    };

    const err = (key) => (submitted ? errors[key] : undefined);

    // ─── Success / Failure screen ──────────────────────────────────────────
    if (createdSummary) {
        const statusLabel = trackingStatus || 'Queued';
        const isTerminal = ['Completed', 'Failed', 'Error', 'Rollback Completed', 'Rollback Failed'].includes(statusLabel);
        const isFailed = ['Failed', 'Error', 'Rollback Failed'].includes(statusLabel);

        return (
            <div style={{
                minHeight: '100vh', background: '#f8fafc',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
                boxSizing: 'border-box',
            }}>
                <div style={{
                    maxWidth: '440px', width: '100%', background: '#fff',
                    borderRadius: '16px', border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 12px 32px rgba(15,23,42,0.06)',
                    padding: '24px', textAlign: 'center', boxSizing: 'border-box'
                }}>
                    <div style={{
                        width: '48px', height: '48px', borderRadius: '50%',
                        background: isFailed ? '#fee2e2' : '#dcfce7', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', margin: '0 auto 16px',
                        boxShadow: isFailed ? '0 0 0 6px rgba(254,226,226,0.5)' : '0 0 0 6px rgba(220,252,231,0.5)',
                    }}>
                        {isFailed
                            ? <AlertCircle size={24} color="#dc2626" strokeWidth={2.2} />
                            : <CheckCircle2 size={24} color="#16a34a" strokeWidth={2.2} />}
                    </div>
                    <h2 style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
                        {isFailed ? 'Registration failed' : 'Registration submitted'}
                    </h2>
                    <p style={{ margin: '0 0 14px', fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                        {createdSummary.companyName} has been submitted as a {createdSummary.accountType} account.
                    </p>
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontSize: '11px', fontWeight: 600, padding: '5px 11px', borderRadius: '999px',
                        marginBottom: '18px',
                        color: isFailed ? '#b91c1c' : (isTerminal ? '#15803d' : '#92400e'),
                        background: isFailed ? '#fef2f2' : (isTerminal ? '#f0fdf4' : '#fffbeb'),
                        border: `1px solid ${isFailed ? '#fecaca' : (isTerminal ? '#bbf7d0' : '#fde68a')}`,
                    }}>
                        {statusPolling && !isTerminal && (
                            <Loader2 size={11} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                        )}
                        Status: {statusLabel}
                    </div>
                    <div style={{
                        textAlign: 'left', background: '#f8fafc', borderRadius: '10px',
                        border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: '20px',
                        fontSize: '12px', color: '#334155', display: 'grid', rowGap: '8px',
                    }}>
                        <SummaryRow label="Request ID" value={createdSummary.requestId} mono />
                        <SummaryRow label="Base Account" value={createdSummary.baseAccount} />
                        <SummaryRow label="Company Prefix" value={createdSummary.prefix} mono />
                        <SummaryRow label="Username" value={createdSummary.username} mono />
                        {createdSummary.procedureCodes && (
                            <SummaryRow label="Procedure Codes" value="Yes" mono />
                        )}
                    </div>
                    <button
                        onClick={handleReset}
                        className="md-btn-primary"
                        style={{
                            width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                            background: THEME.primary, color: THEME.onPrimary, fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        Register another account
                    </button>
                </div>
                {/* This early-return screen renders its own styles (the main <style> block below doesn't apply here) */}
                <style>{`
                    @keyframes masterdata-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    .md-btn-primary { transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease; }
                    .md-btn-primary:hover:not(:disabled) { background: var(--theme-primary-hover) !important; box-shadow: 0 4px 14px rgba(var(--theme-primary-rgb), 0.3); }
                    .md-btn-primary:active:not(:disabled) { transform: translateY(1px); }
                `}</style>
            </div>
        );
    }

    // ─── Main form ─────────────────────────────────────────────────────────
    return (
        <div style={{
            minHeight: '100vh',
            background: '#f6f8fb',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            padding: '16px', boxSizing: 'border-box', position: 'relative',
        }}>
            <form onSubmit={handleSubmit} style={{ maxWidth: '1080px', width: '100%', margin: '0 auto' }}>

                {/* Header & Registration History Bar */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '16px', flexWrap: 'wrap', gap: '12px'
                }}>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                            Account Registration
                        </h1>
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                            Create and configure new system accounts
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setHistoryOpen(true)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #e2e8f0',
                            background: '#fff', color: '#0f172a', fontSize: '12px', fontWeight: 600,
                            cursor: 'pointer', boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                        }}
                    >
                        <History size={14} />
                        Registration History
                    </button>
                </div>

                {submitError && (
                    <div style={{ marginBottom: '12px' }}>
                        <InlineError message={submitError} />
                    </div>
                )}

                <div className="md-main-layout">
                    {/* Left column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <Card>
                            <SectionHeader index="01" title="Account Type" subtitle="Choose the category this company will register as." />
                            {err('accountType') && (
                                <div style={{ marginBottom: '9px' }}>
                                    <InlineError message={errors.accountType} />
                                </div>
                            )}
                            <div className="md-type-grid">
                                {ACCOUNT_TYPES.map((t) => {
                                    const Icon = t.icon;
                                    const active = accountType === t.id;
                                    return (
                                        <button
                                            type="button" key={t.id} onClick={() => handleTypeSelect(t.id)} className="md-type-btn"
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '7px',
                                                padding: '10px 11px 9px', borderRadius: '9px', cursor: 'pointer',
                                                border: active ? `1.5px solid ${t.accent}` : '1.5px solid #e2e8f0',
                                                background: active ? t.accentSoft : '#fff',
                                                boxShadow: active ? `0 2px 8px ${t.accentSoft}` : 'none', textAlign: 'left',
                                                width: '100%', boxSizing: 'border-box'
                                            }}
                                        >
                                            <div style={{
                                                width: '25px', height: '25px', borderRadius: '7px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: active ? t.accent : '#f1f5f9',
                                                flexShrink: 0
                                            }}>
                                                <Icon size={13} color={active ? '#fff' : '#64748b'} strokeWidth={2.2} />
                                            </div>
                                            <span style={{ fontSize: '11px', fontWeight: 600, color: active ? t.accent : '#334155' }}>
                                                {t.label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Card>

                        <Card>
                            <SectionHeader index="04" title="Address" />
                            <Field label="Address Line 1" htmlFor="address1">
                                <IconInput icon={MapPin} id="address1" value={address1} onChange={setAddress1} placeholder="Street address" />
                            </Field>
                            <Field label="Address Line 2" htmlFor="address2">
                                <input id="address2" type="text" value={address2}
                                    onChange={(ev) => setAddress2(ev.target.value)}
                                    placeholder="Suite, floor, etc. (optional)" className="md-input" style={baseInputStyle(false)} />
                            </Field>
                            <div className="md-grid-3">
                                <Field label="City" htmlFor="city">
                                    <input id="city" type="text" value={city} onChange={(ev) => setCity(ev.target.value)}
                                        className="md-input" style={baseInputStyle(false)} />
                                </Field>
                                <Field label="State" required htmlFor="state" error={err('state') ? errors.state : undefined}>
                                    <input id="state" type="text" value={stateVal} onChange={(ev) => setStateVal(ev.target.value)}
                                        placeholder="e.g. CA" maxLength={40} className="md-input" style={baseInputStyle(!!err('state'))} />
                                </Field>
                                <Field label="ZIP" htmlFor="zip">
                                    <input id="zip" type="text" value={zip} onChange={(ev) => setZip(ev.target.value)}
                                        className="md-input" style={baseInputStyle(false)} />
                                </Field>
                            </div>
                            <Field label="Office Phone" htmlFor="officePhone">
                                <input id="officePhone" type="tel" value={officePhone} onChange={(ev) => setOfficePhone(ev.target.value)}
                                    placeholder="(555) 555-0100" className="md-input" style={baseInputStyle(false)} />
                            </Field>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '18px', flexWrap: 'wrap' }}>
                                <div style={{ minWidth: '200px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', paddingTop: '8px' }}>
                                        <input type="checkbox" checked={includeProcedureCodes}
                                            onChange={(ev) => setIncludeProcedureCodes(ev.target.checked)}
                                            style={{ width: '14px', height: '16px', accentColor: THEME.primary, cursor: 'pointer' }} />
                                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                                            Create account with Procedure Codes
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Right column */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0 }}>
                        <Card>
                            <SectionHeader index="02" title="Company Information" />
                            <Field label="Company Name" required htmlFor="companyName" error={err('companyName') ? errors.companyName : undefined}>
                                <input id="companyName" type="text" value={companyName}
                                    onChange={(ev) => setCompanyName(ev.target.value)}
                                    placeholder="e.g. Riverside Diagnostics" className="md-input"
                                    style={baseInputStyle(!!err('companyName'))} />
                            </Field>
                            <Field
                                label="Base Account" required htmlFor="baseAccount"
                                error={err('baseAccount') ? errors.baseAccount : (baseAccountsError || undefined)}
                                hint={!accountType ? 'Select an Account Type to see matching Base Accounts.' : undefined}
                            >
                                <div ref={baseAccountFieldRef} style={{ position: 'relative' }}>
                                    <div style={{ position: 'relative' }}>
                                        <Search size={13} style={{
                                            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                                            color: '#94a3b8', pointerEvents: 'none',
                                        }} />
                                        <input
                                            id="baseAccount" type="text" autoComplete="off"
                                            value={baseAccountDropdownOpen ? baseAccountSearch : (selectedBaseAccountLabel || baseAccountSearch)}
                                            onChange={(ev) => handleBaseAccountInputChange(ev.target.value)}
                                            onFocus={() => { setBaseAccountSearch(''); setBaseAccountDropdownOpen(true); }}
                                            disabled={!accountType || baseAccountsLoading || !!baseAccountsError}
                                            placeholder={
                                                !accountType ? 'Select an Account Type first'
                                                    : baseAccountsLoading ? 'Loading base accounts…'
                                                        : baseAccountOptions.length === 0 ? 'No base accounts found'
                                                            : 'Search by company name or ID…'
                                            }
                                            className="md-input"
                                            style={{
                                                ...baseInputStyle(!!err('baseAccount') || !!baseAccountsError),
                                                cursor: (accountType && !baseAccountsLoading) ? 'text' : 'not-allowed',
                                                color: accountType ? '#0f172a' : '#94a3b8',
                                                paddingLeft: '29px',
                                                paddingRight: baseAccountsLoading ? '30px' : undefined,
                                            }}
                                        />
                                        {baseAccountsLoading && (
                                            <Loader2 size={14} style={{
                                                position: 'absolute', right: '9px', top: '50%',
                                                transform: 'translateY(-50%)', color: '#94a3b8',
                                                animation: 'masterdata-spin 0.8s linear infinite',
                                            }} />
                                        )}
                                    </div>
                                    {baseAccountDropdownOpen && accountType && !baseAccountsLoading && !baseAccountsError && (
                                        <div style={{
                                            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                                            background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px',
                                            boxShadow: '0 4px 16px rgba(15,23,42,0.1)', maxHeight: '220px',
                                            overflowY: 'auto', zIndex: 20,
                                        }}>
                                            {filteredBaseAccountOptions.length === 0 ? (
                                                <div style={{ padding: '9px 11px', fontSize: '12px', color: '#94a3b8' }}>No matching base accounts.</div>
                                            ) : (
                                                filteredBaseAccountOptions.map((ba) => (
                                                    <div key={ba.companyID}
                                                        onMouseDown={(ev) => { ev.preventDefault(); handleBaseAccountSelect(ba); }}
                                                        style={{
                                                            padding: '8px 11px', fontSize: '12px', cursor: 'pointer', color: '#0f172a',
                                                            background: ba.companyID === baseAccount ? '#f1f5f9' : '#fff',
                                                            fontWeight: ba.companyID === baseAccount ? 600 : 400,
                                                        }}
                                                        onMouseEnter={(ev) => { ev.currentTarget.style.background = '#f8fafc'; }}
                                                        onMouseLeave={(ev) => { ev.currentTarget.style.background = ba.companyID === baseAccount ? '#f1f5f9' : '#fff'; }}
                                                    >
                                                        {ba.companyName} <span style={{ color: '#94a3b8' }}>- {ba.companyID}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Field>
                        </Card>

                        <Card>
                            <SectionHeader index="03" title="Contact Information" />
                            <div className="md-grid-2">
                                <Field label="Contact Name" htmlFor="contactName">
                                    <IconInput icon={User} id="contactName" value={contactName} onChange={setContactName} placeholder="Jordan Blake" />
                                </Field>
                                <Field label="Email Address" required htmlFor="email" error={err('email') ? errors.email : undefined}>
                                    <IconInput icon={Mail} id="email" type="email" value={email} onChange={setEmail}
                                        placeholder="contact@company.com" hasError={!!err('email')} />
                                </Field>
                            </div>
                        </Card>

                        <Card>
                            <SectionHeader
                                index="05" title="Company Prefix"
                                subtitle={typeConfig
                                    ? `${typeConfig.label} requires an exact ${typeConfig.prefixLength}-character prefix.`
                                    : 'Select an Account Type to see the required prefix length.'}
                            />
                            <Field label="Company Prefix" required htmlFor="companyPrefix"
                                error={err('companyPrefix') ? errors.companyPrefix : (prefixSuggestError || undefined)}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <input id="companyPrefix" type="text" value={companyPrefix}
                                        onChange={(ev) => applyPrefix(ev.target.value)}
                                        maxLength={typeConfig ? typeConfig.prefixLength : 3}
                                        placeholder={typeConfig ? 'X'.repeat(typeConfig.prefixLength) : 'XX'}
                                        className="md-input"
                                        style={{ ...monoInputStyle(!!err('companyPrefix')), maxWidth: '120px', textTransform: 'uppercase', fontWeight: 700 }} />
                                    {typeConfig && (
                                        <span style={{
                                            fontSize: '10.5px', fontWeight: 600,
                                            // green = "length is valid" (semantic), not the brand colour
                                            color: companyPrefix.length === typeConfig.prefixLength ? '#16a34a' : '#94a3b8',
                                            fontFamily: "'JetBrains Mono', monospace",
                                        }}>
                                            {companyPrefix.length} / {typeConfig.prefixLength}
                                        </span>
                                    )}
                                    {canSuggestPrefix && (
                                        <button type="button" onClick={handleSuggestPrefix} disabled={prefixSuggestLoading}
                                            title="Suggest next available prefix" className="md-btn-secondary"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                fontSize: '11px', fontWeight: 600, color: '#334155',
                                                background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '7px',
                                                cursor: prefixSuggestLoading ? 'not-allowed' : 'pointer',
                                                padding: '6px 11px', opacity: prefixSuggestLoading ? 0.6 : 1,
                                            }}>
                                            {prefixSuggestLoading
                                                ? <Loader2 size={12} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                                                : <Sparkles size={12} />}
                                            Generate prefix
                                        </button>
                                    )}
                                </div>
                            </Field>
                        </Card>

                        <Card>
                            <SectionHeader index="06" title="Application User" subtitle="Username defaults to the prefix + 'Admin', and can be overridden." />
                            <div className="md-grid-2">
                                <Field label="Username" required htmlFor="username"
                                    error={err('username') ? errors.username : (usernameConflict ? 'This username already exists. Enter a unique username.' : undefined)}>
                                    <input id="username" type="text" value={username}
                                        onChange={(ev) => handleUsernameChange(ev.target.value)}
                                        placeholder="e.g. ABAdmin" className="md-input"
                                        style={monoInputStyle(!!err('username') || !!usernameConflict)} />
                                </Field>
                                <Field label="Password" required htmlFor="password" error={err('password') ? errors.password : undefined}>
                                    <IconInput icon={KeyRound} id="password" type="password" value={password}
                                        onChange={setPassword} placeholder="Minimum 8 characters" hasError={!!err('password')} />
                                </Field>
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Submit bar */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginTop: '16px', gap: '12px', flexWrap: 'wrap', flexShrink: 0,
                }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {submitted && Object.keys(errors).length > 0
                            ? `${Object.keys(errors).length} field${Object.keys(errors).length > 1 ? 's' : ''} need attention`
                            : 'Fields marked * are required.'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: '300px', justifyContent: 'flex-end' }}>
                        <button type="button" onClick={handleReset} disabled={submitting} className="md-btn-secondary"
                            style={{
                                flex: 1, padding: '9px 15px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b',
                                cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1,
                                textAlign: 'center'
                            }}>
                            Clear form
                        </button>
                        <button type="submit" disabled={submitting} className="md-btn-primary"
                            style={{
                                flex: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                                padding: '9px 17px', borderRadius: '8px', fontSize: '12px', fontWeight: 700,
                                border: 'none', background: THEME.primary, color: THEME.onPrimary,
                                cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.75 : 1,
                            }}>
                            {submitting
                                ? <Loader2 size={13} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                                : <ChevronRight size={13} />}
                            {submitting ? 'Submitting…' : 'Create Account'}
                        </button>
                    </div>
                </div>
            </form>

            {/* ══════════════════ HISTORY MODAL ══════════════════ */}
            {historyOpen && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px',
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '1280px',
                        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 20px 50px rgba(15,23,42,0.25)', overflow: 'hidden'
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '14px 16px', borderBottom: '1px solid #e2e8f0',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <History size={18} color="#0f172a" />
                                <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>
                                    Registration History
                                </h2>
                            </div>
                            <button type="button" onClick={() => setHistoryOpen(false)} aria-label="Close"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px' }}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="md-filters-bar">
                            <div className="md-filter-item">
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Status</div>
                                <select value={historyFilterStatus} onChange={(e) => setHistoryFilterStatus(e.target.value)}
                                    className="md-input" style={{ ...baseInputStyle(false), width: '100%' }}>
                                    <option value="">All Status</option>
                                    <option value="Queued">Queued</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Failed">Failed</option>
                                    <option value="Rollback Queue">Rollback Queue</option>
                                    <option value="Rollback Processing">Rollback Processing</option>
                                    <option value="Rollback Completed">Rollback Completed</option>
                                    <option value="Rollback Failed">Rollback Failed</option>
                                </select>
                            </div>

                            <div className="md-filter-item">
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Account Type</div>
                                <select value={historyFilterType} onChange={(e) => setHistoryFilterType(e.target.value)}
                                    className="md-input" style={{ ...baseInputStyle(false), width: '100%' }}>
                                    <option value="">All Account Types</option>
                                    {ACCOUNT_TYPES.map((t) => (
                                        <option key={t.id} value={t.id}>{t.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="md-filter-item md-filter-date">
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Date Range</div>
                                <DateRangePicker
                                    value={[
                                        historyDateRange.from ? new Date(historyDateRange.from) : null,
                                        historyDateRange.to ? new Date(historyDateRange.to) : null,
                                    ]}
                                    onChange={(value) => {
                                        if (!value || value.length !== 2) {
                                            setHistoryDateRange({ from: '', to: '' });
                                            return;
                                        }
                                        const [start, end] = value;
                                        setHistoryDateRange({
                                            from: start ? start.toISOString().split('T')[0] : '',
                                            to: end ? end.toISOString().split('T')[0] : '',
                                        });
                                    }}
                                    ranges={predefinedBottomRanges}
                                    placeholder="Select Date Range"
                                    style={{ width: '100%' }}
                                    format="MM/dd/yyyy"
                                    size="sm"
                                    cleanable
                                    placement="bottomStart"
                                    preventOverflow
                                    container={() => document.body}
                                />
                            </div>

                            <div className="md-filter-item md-filter-page">
                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Page Size</div>
                                <select value={historyPageSize} onChange={(e) => setHistoryPageSize(Number(e.target.value))}
                                    className="md-input" style={{ ...baseInputStyle(false), width: '100%' }}>
                                    <option value={10}>10 / page</option>
                                    <option value={20}>20 / page</option>
                                    <option value={50}>50 / page</option>
                                    <option value={100}>100 / page</option>
                                </select>
                            </div>

                            <button type="button" onClick={() => loadHistory(1)} disabled={historyLoading}
                                style={{
                                    padding: '7px 12px', borderRadius: '7px', border: '1.5px solid #e2e8f0',
                                    background: '#f8fafc', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                    height: '34px', flexShrink: 0
                                }}>
                                {historyLoading
                                    ? <Loader2 size={13} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                                    : 'Refresh'}
                            </button>
                        </div>

                        {/* Table container cleanly avoiding horizontal scroll */}
                        <div style={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, padding: '0 8px' }}>
                            {historyLoading && (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                    <Loader2 size={24} style={{ animation: 'masterdata-spin 0.8s linear infinite', color: THEME.primary }} />
                                    <div style={{ marginTop: '8px', fontSize: '13px' }}>Loading records…</div>
                                </div>
                            )}

                            {historyError && (
                                <div style={{ margin: '16px 20px' }}>
                                    <InlineError message={historyError} />
                                </div>
                            )}

                            {!historyLoading && !historyError && historyRecords.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }}>
                                    No registration records found.
                                </div>
                            )}

                            {!historyLoading && historyRecords.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ ...thStyle, width: '14%' }}>Request ID</th>
                                            <th style={{ ...thStyle, width: '13%' }}>Base Company ID</th>
                                            <th style={{ ...thStyle, width: '7%' }}>Company</th>
                                            <th style={{ ...thStyle, width: '13%' }}>Company ID</th>
                                            <th style={{ ...thStyle, width: '7%' }}>User Name</th>
                                            <th style={{ ...thStyle, width: '12%' }}>User ID</th>
                                            <th style={{ ...thStyle, width: '7%' }}>Type</th>
                                            <th style={{ ...thStyle, width: '11%' }}>Status</th>
                                            <th style={{ ...thStyle, width: '5%' }}>Step</th>
                                            <th style={{ ...thStyle, width: '8%' }}>Created</th>
                                            <th style={{ ...thStyle, width: '6%', textAlign: 'center' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyRecords.map((rec) => {
                                            const steps = (rec.step || '').split(',').map((s) => s.trim()).filter(Boolean);
                                            const isExpanded = expandedStepId === rec.requestId;
                                            const canRollback = !['Completed', 'RollbackComplete', 'RollbackProcessing'].includes(rec.status);

                                            return (
                                                <React.Fragment key={rec.requestId}>
                                                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                        <td style={tdStyle} title={rec.requestId || ''}>
                                                            <span style={{ fontFamily: 'monospace', fontSize: '10.5px', wordBreak: 'break-all' }}>
                                                                {rec.requestId || '—'}
                                                            </span>
                                                        </td>
                                                        <td title={rec.baseCompanyID || ''} style={{ ...tdStyle, wordBreak: 'break-all' }}>{rec.baseCompanyID || '—'}</td>
                                                        <td title={rec.companyName || ''} style={{ ...tdStyle, wordBreak: 'break-word' }}>{rec.companyName || '—'}</td>
                                                        <td title={rec.newCompanyID || ''} style={{ ...tdStyle, wordBreak: 'break-all' }}>{rec.newCompanyID || '—'}</td>
                                                        <td title={rec.userName || ''} style={{ ...tdStyle, wordBreak: 'break-word' }}>{rec.userName || '—'}</td>
                                                        <td title={rec.userId || ''} style={{ ...tdStyle, wordBreak: 'break-all' }}>{rec.userId || '—'}</td>
                                                        <td title={rec.accountTypeKey || ''} style={tdStyle}>{rec.accountTypeKey || '—'}</td>
                                                        <td title={rec.status || ''} style={tdStyle}><StatusBadge status={rec.status} /></td>
                                                        <td style={tdStyle}>
                                                            {steps.length > 0 ? (
                                                                <button type="button"
                                                                    onClick={() => setExpandedStepId(isExpanded ? null : rec.requestId)}
                                                                    aria-expanded={isExpanded}
                                                                    aria-label={`${steps.length} steps`}
                                                                    style={{
                                                                        display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                                        background: isExpanded ? THEME.primaryLight : '#f1f5f9',
                                                                        border: `1px solid ${isExpanded ? THEME.alpha(0.35) : '#e2e8f0'}`,
                                                                        borderRadius: '5px',
                                                                        padding: '2px 6px', fontSize: '10.5px', cursor: 'pointer',
                                                                        color: isExpanded ? THEME.primaryHover : '#334155',
                                                                    }}>
                                                                    <List size={11} />
                                                                    {steps.length}
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: '#94a3b8' }}>—</span>
                                                            )}
                                                        </td>
                                                        <td style={{ ...tdStyle, fontSize: '10.5px', lineHeight: 1.2 }}>
                                                            {rec.createdAt ? new Date(rec.createdAt).toLocaleString() : '—'}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                            <button type="button"
                                                                disabled={rollingBackId === rec.requestId || !canRollback}
                                                                onClick={() => requestRollback(rec)}
                                                                title={!canRollback ? 'Cannot rollback this status' : 'Rollback this registration'}
                                                                style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                                                    padding: '3px 7px', borderRadius: '5px', border: '1px solid #fecaca',
                                                                    background: rollingBackId === rec.requestId ? '#fee2e2' : '#fef2f2',
                                                                    color: '#b91c1c', fontSize: '10.5px', fontWeight: 600,
                                                                    cursor: (rollingBackId === rec.requestId || !canRollback) ? 'not-allowed' : 'pointer',
                                                                    opacity: !canRollback ? 0.5 : 1,
                                                                }}>
                                                                {rollingBackId === rec.requestId
                                                                    ? <Loader2 size={11} style={{ animation: 'masterdata-spin 0.8s linear infinite' }} />
                                                                    : <RotateCcw size={11} />}
                                                                Rollback
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={11} style={{ background: '#f8fafc', padding: '10px 16px' }}>
                                                                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                                                                    Steps performed:
                                                                </div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                                    {steps.map((step, idx) => {
                                                                        // Step chips: purple = SP step, blue = other step (category colours, not themed)
                                                                        const isSpStep = /^SP:/i.test(step.trim());
                                                                        return (
                                                                            <span key={idx} style={{
                                                                                background: isSpStep ? '#f3e8ff' : '#e0f2fe',
                                                                                color: isSpStep ? '#7e22ce' : '#0369a1',
                                                                                borderRadius: '999px',
                                                                                padding: '3px 10px', fontSize: '11px', fontWeight: 500,
                                                                            }}>
                                                                                {step.replace(/ completed,?$/i, '')}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                                {rec.errorMessage && (
                                                                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#b91c1c' }}>
                                                                        <strong>Error:</strong> {rec.errorMessage}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Pagination */}
                        {!historyLoading && historyRecords.length > 0 && (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '12px 16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '8px'
                            }}>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    Page <strong>{historyPage}</strong>
                                    {historyHasMore ? ' (more available)' : ' (last page)'}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button type="button"
                                        disabled={historyPage <= 1 || historyLoading}
                                        onClick={() => loadHistory(historyPage - 1)}
                                        style={{
                                            padding: '6px 12px', borderRadius: '6px', border: '1.5px solid #e2e8f0',
                                            background: historyPage <= 1 ? '#f1f5f9' : '#fff',
                                            fontSize: '12px', fontWeight: 600,
                                            cursor: historyPage <= 1 ? 'not-allowed' : 'pointer',
                                            color: historyPage <= 1 ? '#94a3b8' : '#0f172a',
                                        }}>
                                        Previous
                                    </button>
                                    <button type="button"
                                        disabled={!historyHasMore || historyLoading}
                                        onClick={() => loadHistory(historyPage + 1)}
                                        style={{
                                            padding: '6px 12px', borderRadius: '6px', border: '1.5px solid #e2e8f0',
                                            background: !historyHasMore ? '#f1f5f9' : '#fff',
                                            fontSize: '12px', fontWeight: 600,
                                            cursor: !historyHasMore ? 'not-allowed' : 'pointer',
                                            color: !historyHasMore ? '#94a3b8' : '#0f172a',
                                        }}>
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════════ Rollback confirm / result dialogs ══════════════ */}
            <RollbackConfirmDialog
                record={rollbackConfirmRecord}
                busy={!!rollingBackId}
                onCancel={cancelRollbackConfirm}
                onConfirm={confirmRollback}
            />
            <RollbackResultDialog
                result={rollbackResult}
                onClose={closeRollbackResult}
            />

            <style>{`
        @keyframes masterdata-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes md-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes md-pop-in {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        /* Main Layout Grid */
        .md-main-layout {
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;
            align-items: start;
        }

        @media (min-width: 850px) {
            .md-main-layout {
                grid-template-columns: 1fr 1fr;
                gap: 14px;
            }
        }

        /* Responsive Form Grids */
        .md-grid-2 {
            display: grid;
            grid-template-columns: 1fr;
            gap: 10px;
        }
        @media (min-width: 480px) {
            .md-grid-2 {
                grid-template-columns: 1fr 1fr;
            }
        }

        .md-grid-3 {
            display: grid;
            grid-template-columns: 1fr;
            gap: 10px;
        }
        @media (min-width: 480px) {
            .md-grid-3 {
                grid-template-columns: 1.4fr 1fr 1fr;
            }
        }

        /* Account Type Grid */
        .md-type-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
        }
        @media (min-width: 380px) {
            .md-type-grid {
                grid-template-columns: repeat(3, 1fr);
            }
        }
        @media (min-width: 600px) {
            .md-type-grid {
                grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
            }
        }

        /* Modal Filters Responsive Bar */
        .md-filters-bar {
            display: flex;
            gap: 10px;
            padding: 12px 16px;
            border-bottom: 1px solid #f1f5f9;
            flex-wrap: wrap;
            align-items: flex-end;
        }
        .md-filter-item {
            flex: 1 1 140px;
            min-width: 130px;
        }
        .md-filter-date {
            flex: 2 1 220px;
            min-width: 200px;
        }
        .md-filter-page {
            flex: 1 1 90px;
            min-width: 90px;
        }

        /* Button & Control Animations — primary button + focus ring follow the theme */
        .md-card { transition: box-shadow 0.18s ease, border-color 0.18s ease; }
        .md-card:hover { border-color: #dbe2ea; box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 6px 18px rgba(15,23,42,0.05); }
        .md-input { transition: border-color 0.15s ease, box-shadow 0.15s ease; }
        .md-input:hover { border-color: #cbd5e1; }
        .md-input:focus { border-color: var(--theme-primary) !important; box-shadow: 0 0 0 3.5px rgba(var(--theme-primary-rgb), 0.15); }
        .md-input:disabled { cursor: not-allowed; }
        .md-type-btn { transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease; }
        .md-type-btn:hover { border-color: #cbd5e1; transform: translateY(-1px); }
        .md-type-btn:active { transform: translateY(0); }
        .md-btn-primary { transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease; }
        .md-btn-primary:hover:not(:disabled) { background: var(--theme-primary-hover) !important; box-shadow: 0 4px 14px rgba(var(--theme-primary-rgb), 0.3); }
        .md-btn-primary:active:not(:disabled) { transform: translateY(1px); }
        .md-btn-secondary { transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
        .md-btn-secondary:hover:not(:disabled) { background: #f1f5f9 !important; border-color: #cbd5e1 !important; color: #334155; }
        .md-btn-danger { transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease; }
        .md-btn-danger:hover:not(:disabled) { background: #b91c1c !important; box-shadow: 0 4px 14px rgba(220,38,38,0.25); }
        .md-btn-danger:active:not(:disabled) { transform: translateY(1px); }

        /* DateRangePicker menu positioning */
        .rs-picker-menu,
        .rs-picker-daterange-menu {
          z-index: 2000 !important;
        }
      `}</style>
        </div>
    );
}